<template>
	<ViewLayout>
		<template #header-title>
			Action Links
		</template>
		<template #header-after>
			<Tabs
				:tab.sync="tab"
				:tabs="tabs"
			/>
		</template>
		<template #content>
			<TabItems
				:tab="tab"
				:tabs="tabs"
			>
				<template #0>
					<ActionLinkTab
						:action-links="actionLinks"
						:user-full-permissions="userFullPermissions"
						:user-read-only="userReadOnly"
						:link-auth-types="linkAuthTypes"
						:status-filter-items="statusFilterItems"
						@create-link="createLink"
						@edit-link="onEditLink"
						@clone-link="onCloneLink"
						@view-link="onViewLink"
						@remove-link="onRemoveLink"
						@load-links="loadActionLinks"
						@preview-link="onPreviewLink"
					/>
				</template>
				<template #1>
					<LinkTranslationsTab
						:user-full-permissions="userFullPermissions"
						:user-read-only="userReadOnly"
						:action-links="actionLinksTranslations"
						:link-types="lookUpData.linkTypes"
						@load-links="loadLinks"
					/>
				</template>
			</TabItems>
			<RemoveLinkModal
				v-if="linkToBeRemoved"
				v-bind="linkToBeRemoved"
				@close="linkToBeRemoved = null"
				@deleted="loadActionLinks"
			/>
			<ViewLinkModal
				v-if="linkToView"
				v-bind="linkToView"
				:assigned-language-items="assignedLanguagesItems"
				@close="linkToView = null"
			/>
			<ActionLinkTypeSelectionModal
				v-if="showLinkTypeModal"
				@close="showLinkTypeModal = false"
				@select="onLinkTypeSelected"
			/>
		</template>
		<template #footer>
			<PageActionRow>
				<template #actions>
					<SecondaryActionButton @click="backToConsentCollection">
						Back to Consent Collection
					</SecondaryActionButton>
				</template>
			</PageActionRow>
		</template>
	</ViewLayout>
</template>
<script>
import { mapGetters } from 'vuex'
import { showSnackbar } from '../../../../../../shared/state/snackbar.js'
import ViewLayout from '../../../../../../shared/layouts/view-layout.vue'
import Tabs from '../../../../../../shared/components/tabs.vue'
import TabItems from '../../../../../../shared/components/tab-items.vue'
import ActionLinkTab from './action-link-tab.vue'
import LinkTranslationsTab from '../shared/link-translations-tab.vue'
import PageActionRow from '../../../../../../shared/components/page-action-row.vue'
import SecondaryActionButton from '../../../../../../shared/components/secondary-action-button.vue'
import { getLinks, getLinkButtonTranslations, encryptParameters, getActionLinks, getActionLinkEmbedCode, getLinkByID, getActionLinkEmbedCodePreview } from '../../../../../../shared/utils/api/admin-portal/links/links.js'
import RemoveLinkModal from '../remove-link-modal.vue'
import { MANAGE_PREFERENCE_LINK, CONSENT_COLLECTION } from '../../../../router/route-names.js'
import ViewLinkModal from '../view-link-modal.vue'
import ActionLinkTypeSelectionModal from './action-link-type-selection-modal.vue'
import { SEE_LINKS_AREA, LINKS_AREA_FULL_PERMISSIONS, LINKS_PAGE_FULL_PERMISSIONS } from '../../../../../../shared/permissions/admin-portal-permissions.js'
import { getAssignedLanguages } from '../../../../../../shared/utils/api/languages.js'
import previewLinkMixin from '../../../../../../shared/mixins/preview-link.js'

export default {
	components: {
		ViewLayout,
		Tabs,
		TabItems,
		ActionLinkTab,
		ViewLinkModal,
		RemoveLinkModal,
		LinkTranslationsTab,
		PageActionRow,
		SecondaryActionButton,
		ActionLinkTypeSelectionModal
	},
	mixins: [previewLinkMixin],
	props: {
		accessedViaTranslations: Boolean
	},
	setup () {
		return {
			showSnackbar
		}
	},
	data () {
		return {
			tabs: [
				{ title: 'Action Links', slot: '0', icon: 'mdi-link' },
				{ title: 'Action Link Translations', slot: '1', icon: 'mdi-translate' }],
			tab: '0',
			actionLinks: [],
			actionLinksTranslations: [],
			preferencePageLinks: [],
			lookUpData: [],
			linkToBeRemoved: null,
			linkToView: null,
			selectedBrandId: null,
			assignedLanguages: [],
			linksButtons: null,
			changeMadeToGeneralTranslations: false,
			showLinkTypeModal: false
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission', 'userHasUniversalBrand']),
		userFullPermissions () {
			return this.productAreaPermission(LINKS_AREA_FULL_PERMISSIONS) || this.productAreaPermission(LINKS_PAGE_FULL_PERMISSIONS)
		},
		userReadOnly () {
			return this.productAreaPermission(SEE_LINKS_AREA)
		},
		linkAuthTypes () {
			const linkAuthTypes = {}
			for (const link of this.actionLinks) {
				if (!linkAuthTypes[link.authenticationTypeId]) {
					linkAuthTypes[link.authenticationTypeId] = link.authenticationType
				}
			}
			return Object.keys(linkAuthTypes).map(
				key => ({ value: key, text: linkAuthTypes[key] })
			)
		},
		assignedLanguagesItems () {
			return this.assignedLanguages.map(language => ({
				...language,
				languageCode: language.languageID === 0 ? 'default' : language.languageCode,
				value: language.languageID,
				text: language.languageName
			}))
		},
		statusFilterItems () {
			return [
				{ value: null, text: 'View All' },
				{ value: true, text: 'Enabled' },
				{ value: false, text: 'Disabled' }
			]
		}
	},
	created () {
		this.loadLanguages()
		this.loadActionLinks()
		this.loadLinksButtonTranslations()
		if (this.accessedViaTranslations) {
			this.tab = '1'
		} else {
			this.tab = '0'
		}
	},
	methods: {
		async loadActionLinks () {
			this.actionLinks = await getActionLinks()
			this.actionLinksTranslations = (await getLinks()).data.actionLinks
		},
		async loadLinks () {
			const mapAuthenticationType = links => links.map(link => ({ ...link, authenticationType: link.authenticationDetails?.authTypeDescription }))
			const { data: { actionLinks, lookUpData } } = await getLinks()
			this.lookUpData = lookUpData
			this.actionLinks = mapAuthenticationType(actionLinks)
		},
		async onEditLink (link, linkType) {
			const { data } = await getLinkByID(link.linkId)
			const actionLink = data.actionLinks[0]
			if (!actionLink.linkFields.length) {
				showSnackbar({ color: 'red', text: 'this link cannot be edited within this portal as it has no StandDak parameters' })
			} else {
				this.$router.push({
					name: MANAGE_PREFERENCE_LINK,
					params: {
						linkToEdit: actionLink,
						assignedLanguageItems: this.assignedLanguagesItems
					},
					query: {
						linkType
					}
				})
			}
		},
		async onCloneLink (link, linkType) {
			const { data } = await getLinkByID(link.linkId)
			const actionLink = data.actionLinks[0]
			const linkToClone = {
				...actionLink,
				linkName: `${actionLink.linkName} (clone)`
			}
			delete linkToClone.linkId
			if (!actionLink.linkFields.length) {
				showSnackbar({ color: 'red', text: 'this link cannot be cloned within this portal as it has no StandDak parameters' })
			} else {
				this.$router.push({
					name: MANAGE_PREFERENCE_LINK,
					params: {
						linkToEdit: linkToClone,
						assignedLanguageItems: this.assignedLanguagesItems,
						cloneMode: true
					},
					query: {
						linkType
					}
				})
			}
		},
		async onViewLink (link) {
			this.linkToView = await getActionLinkEmbedCode(link.linkId)
			this.linkToView.linkName = link.linkName
		},
		onRemoveLink (link) {
			this.linkToBeRemoved = link
		},
		createLink () {
			this.showLinkTypeModal = true
		},
		onLinkTypeSelected ({ isOneClickUnsubscribe }) {
			this.showLinkTypeModal = false
			this.$router.push({
				name: MANAGE_PREFERENCE_LINK,
				params: {
					assignedLanguageItems: this.assignedLanguagesItems
				},
				query: {
					linkType: 1,
					isOneClickUnsubscribe: isOneClickUnsubscribe ? 'true' : undefined
				}
			})
		},
		async loadLanguages () {
			const { data: assignedLanguages } = await getAssignedLanguages()
			this.assignedLanguages = assignedLanguages
		},
		async loadLinksButtonTranslations () {
			// const { data: { linkButtonTranslations } } = await getLinkButtonTranslations()
			const linksButtons = await getLinkButtonTranslations()
			this.linksButtons = linksButtons.data
		},
		generalTranslationsChanged (changeMade) {
			this.changeMadeToGeneralTranslations = changeMade
		},
		saveGeneralTranslations () {
			this.$refs.generalTranslationsTab.saveTranslations()
		},
		cancelGeneralTranslationChanges () {
			this.$refs.generalTranslationsTab.cancelTranslationChanges()
		},
		async onPreviewLink (link) {
			const previewLink = await getActionLinkEmbedCodePreview(link.linkId)
			const tempString = this.getLinkHref(previewLink.previewLink)
			window.open(tempString)
		},
		async getEncryptedData (encryptedParams, link) {
			return await encryptParameters({
				encryptionKeyId: link.configurationOptions.encryption.encryptionKeyId,
				linkParams: encryptedParams
			})
		},
		getLinkHref (previewLink) {
			return new DOMParser()
				.parseFromString(previewLink, 'text/html')
				.getElementsByTagName('a')[0].href
		},
		backToConsentCollection () {
			this.$router.push({
				name: CONSENT_COLLECTION
			})
		}
	}
}
</script>
